import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Activity,
  Calendar,
  TrendingUp,
  CheckCircle2,
  Stethoscope,
  Info,
  ChevronRight,
  MessageSquare,
  Droplets,
  Zap,
  Wind,
  Mountain
} from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
  Radar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis
} from 'recharts';
import { Layout } from '@/components/Layout';
import {
  CONSTITUTION_TYPES,
  ConstitutionType,
  ElementType,
  ROUTE_PATHS
} from '@/lib/index';
import { IMAGES } from '@/assets/images';

// Mock Data for Dashboard
const intakeData = [
  { day: '월', status: 'completed' },
  { day: '화', status: 'completed' },
  { day: '수', status: 'completed' },
  { day: '목', status: 'missed' },
  { day: '금', status: 'completed' },
  { day: '토', status: 'pending' },
  { day: '일', status: 'pending' },
];

const healthMetrics = [
  { date: '01.15', muscle: 32.5, weight: 78.2 },
  { date: '01.22', muscle: 32.8, weight: 77.8 },
  { date: '01.29', muscle: 33.1, weight: 77.5 },
  { date: '02.05', muscle: 33.4, weight: 77.2 },
  { date: '02.12', muscle: 33.2, weight: 77.0 },
  { date: '02.19', muscle: 33.6, weight: 76.8 },
  { date: '02.26', muscle: 34.0, weight: 76.5 },
];

const organBalanceData = [
  { subject: '간(肝)', A: 90, fullMark: 100 },
  { subject: '폐(肺)', A: 45, fullMark: 100 },
  { subject: '비(脾)', A: 60, fullMark: 100 },
  { subject: '신(腎)', A: 75, fullMark: 100 },
];

const feedbackScores = [
  { name: '소화 편안함', score: 85 },
  { name: '활력 지수', score: 78 },
  { name: '복용 지속성', score: 92 },
];

export default function Management() {
  const [currentConstitution] = useState<ConstitutionType>('태음인');
  const constitutionInfo = CONSTITUTION_TYPES[currentConstitution];

  const getElementIcon = (element: ElementType) => {
    switch (element) {
      case '地': return <Mountain className="w-5 h-5" />;
      case '水': return <Droplets className="w-5 h-5" />;
      case '火': return <Zap className="w-5 h-5" />;
      case '風': return <Wind className="w-5 h-5" />;
      default: return null;
    }
  };

  return (
    <Layout>
      <div className="min-h-screen bg-background pb-20">
        {/* Dashboard Header */}
        <section className="bg-primary text-primary-foreground py-12">
          <div className="container mx-auto px-4">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
              <div>
                <motion.h1 
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-3xl font-bold mb-2"
                >
                  김민수 님의 체질 대시보드
                </motion.h1>
                <div className="flex items-center gap-3">
                  <span className="bg-white/20 px-3 py-1 rounded-full text-sm font-medium flex items-center gap-2">
                    {getElementIcon(constitutionInfo.element)}
                    {constitutionInfo.name}
                  </span>
                  <span className="text-white/80 text-sm">
                    최근 진단일: 2026.01.10
                  </span>
                </div>
              </div>
              <button className="bg-white text-primary px-6 py-2 rounded-full font-semibold hover:bg-white/90 transition-colors flex items-center gap-2">
                <Activity className="w-4 h-4" />
                체질 재진단하기
              </button>
            </div>
          </div>
        </section>

        <div className="container mx-auto px-4 -mt-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            
            {/* Left Column: Progress & Feedback */}
            <div className="lg:col-span-2 space-y-6">
              {/* Intake Progress Card */}
              <motion.div 
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-card p-6 rounded-2xl border border-border shadow-sm"
              >
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-bold flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-primary" />
                    이번 주 단백환 섭취 현황
                  </h2>
                  <span className="text-sm text-muted-foreground font-mono">달성률 85%</span>
                </div>
                <div className="grid grid-cols-7 gap-2">
                  {intakeData.map((item, idx) => (
                    <div key={idx} className="flex flex-col items-center gap-2">
                      <span className="text-xs text-muted-foreground">{item.day}</span>
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all ${
                        item.status === 'completed' ? 'bg-primary text-white' : 
                        item.status === 'missed' ? 'bg-destructive/10 text-destructive border border-destructive/20' : 
                        'bg-muted text-muted-foreground'
                      }`}>
                        {item.status === 'completed' && <CheckCircle2 className="w-6 h-6" />}
                        {item.status === 'pending' && <span className="text-xs font-bold">{idx + 1}</span>}
                        {item.status === 'missed' && <span className="text-xs font-bold">!</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>

              {/* Muscle Mass Chart Card */}
              <motion.div 
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.1 }}
                className="bg-card p-6 rounded-2xl border border-border shadow-sm"
              >
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-bold flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-primary" />
                    근육량 및 체성분 변화
                  </h2>
                  <div className="flex gap-4">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 bg-primary rounded-full" />
                      <span className="text-xs text-muted-foreground">근육량 (kg)</span>
                    </div>
                  </div>
                </div>
                <div className="h-64 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={healthMetrics}>
                      <defs>
                        <linearGradient id="colorMuscle" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="var(--primary)" stopOpacity={0.1}/>
                          <stop offset="95%" stopColor="var(--primary)" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border)" />
                      <XAxis 
                        dataKey="date" 
                        axisLine={false} 
                        tickLine={false} 
                        tick={{ fontSize: 12, fill: 'var(--muted-foreground)', fontFamily: 'var(--font-mono)' }} 
                      />
                      <YAxis 
                        axisLine={false} 
                        tickLine={false} 
                        tick={{ fontSize: 12, fill: 'var(--muted-foreground)', fontFamily: 'var(--font-mono)' }}
                        domain={[30, 36]}
                      />
                      <Tooltip 
                        contentStyle={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)', borderRadius: '12px' }}
                      />
                      <Area 
                        type="monotone" 
                        dataKey="muscle" 
                        stroke="var(--primary)" 
                        fillOpacity={1} 
                        fill="url(#colorMuscle)" 
                        strokeWidth={3}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
                <p className="mt-4 text-sm text-muted-foreground flex items-center gap-2">
                  <Info className="w-4 h-4" />
                  지난 4주간 근육량이 약 1.5kg 증가했습니다. 체질에 맞는 단백질 흡수가 원활합니다.
                </p>
              </motion.div>

              {/* Intake Feedback Summary */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {feedbackScores.map((item, idx) => (
                  <div key={idx} className="bg-card p-4 rounded-xl border border-border shadow-sm">
                    <span className="text-xs text-muted-foreground block mb-1">{item.name}</span>
                    <div className="flex items-end gap-2">
                      <span className="text-2xl font-bold font-mono text-primary">{item.score}%</span>
                      <span className="text-xs text-green-600 mb-1">+2%</span>
                    </div>
                    <div className="w-full bg-muted h-1.5 rounded-full mt-2">
                      <div className="bg-primary h-full rounded-full" style={{ width: `${item.score}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right Column: Constitution & Clinic */}
            <div className="space-y-6">
              {/* Constitution Radar Card */}
              <motion.div 
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="bg-card p-6 rounded-2xl border border-border shadow-sm"
              >
                <h2 className="text-lg font-bold mb-4">장부 허실 분석</h2>
                <div className="h-48 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <RadarChart cx="50%" cy="50%" outerRadius="70%" data={organBalanceData}>
                      <PolarGrid stroke="var(--border)" />
                      <PolarAngleAxis dataKey="subject" tick={{ fontSize: 11 }} />
                      <Radar
                        name="김민수"
                        dataKey="A"
                        stroke="var(--primary)"
                        fill="var(--primary)"
                        fillOpacity={0.6}
                      />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>
                <div className="mt-4 p-3 bg-muted/50 rounded-lg text-xs leading-relaxed">
                  <strong className="text-primary">{constitutionInfo.organBalance.strong}</strong> 기운이 강하고 <strong className="text-destructive">{constitutionInfo.organBalance.weak}</strong> 기운이 약한 체질입니다. 기운을 안으로 모으는 <span className="font-bold">저장(Storage)</span>의 성질을 보완하기 위해 <strong>地(지)</strong> 타입 단백질을 권장합니다.
                </div>
              </motion.div>

              {/* Clinic Link Card */}
              <motion.div 
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 }}
                className="bg-card p-6 rounded-2xl border border-border shadow-sm overflow-hidden relative"
              >
                <img 
                  src={IMAGES.HEALTH_CONSULTATION_1} 
                  alt="Clinic"
                  className="absolute inset-0 w-full h-full object-cover opacity-10"
                />
                <div className="relative z-10">
                  <div className="flex items-center gap-2 text-primary font-bold mb-3">
                    <Stethoscope className="w-5 h-5" />
                    연계 한의원 정보
                  </div>
                  <h3 className="text-xl font-bold mb-1">경희 바른 한의원</h3>
                  <p className="text-sm text-muted-foreground mb-4">담당의: 이지훈 원장</p>
                  <div className="space-y-2">
                    <button className="w-full py-2 bg-primary text-primary-foreground rounded-lg text-sm font-semibold flex items-center justify-center gap-2">
                      <MessageSquare className="w-4 h-4" />
                      진료 상담 예약하기
                    </button>
                    <button className="w-full py-2 bg-secondary text-secondary-foreground rounded-lg text-sm font-semibold flex items-center justify-center gap-2">
                      섭취 가이드 확인
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </motion.div>

              {/* Next Delivery Card */}
              <div className="bg-primary/5 border border-primary/10 p-5 rounded-2xl">
                <h4 className="text-sm font-semibold text-primary mb-2">다음 정기 배송 안내</h4>
                <div className="flex justify-between items-end">
                  <div>
                    <p className="text-2xl font-bold font-mono">03. 12</p>
                    <p className="text-xs text-muted-foreground">地(지) 타입 단백환 30일분</p>
                  </div>
                  <button className="text-xs font-semibold text-primary hover:underline">
                    변경하기
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
